import {
  Button,
  Card,
  Col,
  CreateButton,
  DatePicker,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Row,
  Select,
  Space,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import { CustomTag } from "components/extra/custom-tag";
import { DefaultMaskedInput } from "components/extra/default-masked-input";
import dayjs from "dayjs";
import { ShootBlockingOperation } from "enums";
import { ADMIN_BFF_URL } from "environment";
import { useInfiniteSelect } from "hooks/useInfiniteSelect";
import { IShootBlocked } from "interfaces";
import { IPartner } from "interfaces/partner";
import { FC, useState } from "react";
import { shootBlockingOperationMap } from "utils/constants";
import { exportCSV } from "utils/exports";
import { formatPhone } from "utils/formaters";

interface IShootBlockingFilter {
  phone: string;
  template: string;
  partner: string;
  operation: string;
  origin: string;
  received_date: string;
  expiration_date: string;
}

function normalizeSearchParams(params: IShootBlockingFilter) {
  return {
    phone: params.phone?.replace(/\D/g, ""),
    template: params.template,
    partner: params.partner,
    operation: params.operation,
    origin: params.origin,
    received_date: params.received_date
      ? dayjs(params.received_date).format("YYYY-MM-DD")
      : null,
    expiration_date: params.expiration_date
      ? dayjs(params.expiration_date).format("YYYY-MM-DD")
      : null,
  };
}

export const ShootBlockingList: FC<IResourceComponentsProps> = () => {
  const [enabled, setEnabled] = useState(false);
  const { tableProps, sorter, searchFormProps } = useTable<
    IShootBlocked,
    HttpError,
    IShootBlockingFilter
  >({
    resource: "v1/bot-integration/prospect/receive/list",
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: enabled,
    onSearch: (values) => {
      const params = normalizeSearchParams(values);
      const filtros: CrudFilters = [
        {
          field: "phone",
          operator: "eq",
          value: params.phone,
        },
        { field: "template", operator: "eq", value: params.template },
        { field: "partner", operator: "eq", value: params.partner },
        { field: "operation", operator: "eq", value: params.operation },
        {
          field: "received_date",
          operator: "eq",
          value: params.received_date,
        },
        {
          field: "expiration_date",
          operator: "eq",
          value: params.expiration_date,
        },
      ];

      const hasFilters = filtros.some((f) => f.value);
      setEnabled(hasFilters);

      return filtros;
    },
    queryOptions: { enabled },
  });

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setEnabled(false);
  };

  const handleExportCSV = () => {
    const params =
      searchFormProps.form?.getFieldsValue() as IShootBlockingFilter;

    exportCSV({
      url: ADMIN_BFF_URL + "/v1/bot-integration/prospect/receive/list/csv/",
      filenamePrefix: "bloqueio-de-disparos",
      params: normalizeSearchParams(params),
    });
  };

  
  const { selectProps: selectPropsPartners } = useInfiniteSelect<IPartner>({
      resource: "v1/partners",
      optionLabel: "partner_name",
      optionValue: "partner_code",
      sort: [{ field: "partner_name", order: "asc" }],
      pageSize: 20
  });

  const partnerOptions = selectPropsPartners.options?.map((opt) => ({
    ...opt,
    label: `${opt.label}`,
  })) ?? [];
    
  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Button
              icon={<Icons.DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={!tableProps?.dataSource?.length}
            >
              Exportar CSV
            </Button>

            <CreateButton type="primary">Incluir Bloqueio</CreateButton>
          </Space>
        ),
      }}
    >
      <Card title="Filtrar">
        <Form {...searchFormProps} layout="vertical" style={{ padding: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="phone"
                label="Telefone"
                rules={[
                  { min: 19, message: "Telefone inválido", required: false },
                ]}
              >
                <DefaultMaskedInput
                  mask="+55 (00) 00000-0000"
                  className="input-custom ant-input"
                  placeholder="Telefone"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="template" label="Nome do Template">
                <Input placeholder="Nome do Template" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="partner" label="Parceiro">
                <Select
                  {...selectPropsPartners}
                    options={partnerOptions}
                    placeholder="Parceiro"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="operation" label="Motivo">
                <Select placeholder="Motivo" allowClear>
                  {Object.entries(ShootBlockingOperation).map(
                    ([key, value]) => (
                      <Select.Option key={key} value={value}>
                        {shootBlockingOperationMap[value].label}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="received_date" label="Data de Inclusão">
                <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="expiration_date" label="Data de Validade">
                <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
              <Space>
                <Button
                  type="default"
                  icon={<Icons.ClearOutlined />}
                  onClick={handleCleanFilters}
                >
                  Limpar
                </Button>
                <Button
                  type="primary"
                  icon={<Icons.SearchOutlined />}
                  htmlType="submit"
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        {...tableProps}
        rowKey="_id"
        dataSource={
          enabled && !tableProps.loading ? tableProps?.dataSource : []
        }
        loading={enabled ? tableProps.loading : false}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
      >
        <Table.Column
          dataIndex="phone"
          key="phone"
          title="Telefone"
          render={(value) => (
            <TextField value={value ? formatPhone(value) : "-"} />
          )}
        />

        <Table.Column
          dataIndex="template"
          key="temlpate"
          title="Nome do Template"
          render={(value) => <TextField value={value || "-"} />}
        />

        <Table.Column
          dataIndex="partner"
          key="partner"
          title="Parceiro"
          render={(value) => <TextField value={value || "-"} />}
        />

        <Table.Column<IShootBlockingFilter>
          dataIndex="operation"
          key="operation"
          title="Motivo"
          render={(value, record) => {
            const sufixMap: Record<string, string> = {
              gupshup: "automatic",
              admin: "manual",
            };
            const needsOrigin = ["blocklist", "reactivation"].includes(value);
            const suffix = sufixMap[record.origin];

            const operationKey = needsOrigin ? `${value}-${suffix}` : value;

            const { color, label } =
              shootBlockingOperationMap[
                operationKey as ShootBlockingOperation
              ] || {};

            return <CustomTag color={color} label={label} />;
          }}
        />

        <Table.Column
          dataIndex="received_datetime"
          key="received_datetime"
          title="Data de Inclusão"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"}
            />
          )}
          defaultSortOrder={getDefaultSortOrder("received_datetime", sorter)}
          sorter
        />

        <Table.Column
          dataIndex="expiration_datetime"
          key="expiration_datetime"
          title="Data de Validade"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"}
            />
          )}
          defaultSortOrder={getDefaultSortOrder("expiration_datetime", sorter)}
          sorter
        />
      </Table>
    </List>
  );
};
